import { useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { aiqApi } from '@/api/aiq'
import { learnerApi } from '@/api/b2b'
import { ArrowLeft, Loader2, AlertCircle } from 'lucide-react'
import { AiqIndividualReportView } from '@quebon/shared-learning'
import { useAuthStore } from '@/store/authStore'
import { isDevToolsVisible } from '@/utils/devTools'
import { isAiqReportDateLocked } from '@/config/aiqReportGate'

export default function AiqReport() {
  const { classProductId } = useParams<{ classProductId: string }>()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const canBypassGate = isDevToolsVisible(user?.email)

  useEffect(() => {
    if (!canBypassGate && isAiqReportDateLocked()) {
      navigate('/my/aiq', { replace: true })
    }
  }, [canBypassGate, navigate])

  // Get course info for productId
  const { data: coursesData, isLoading: isLoadingCourse } = useQuery({
    queryKey: ['myClassCourses'],
    queryFn: () => learnerApi.getMyClassCourses(),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })

  const courses = coursesData?.data?.data || []
  const currentCourse = courses.find(c => c.classProductId === Number(classProductId))
  const productId = currentCourse?.productId

  // Fetch report data
  const { data: reportData, isLoading: isLoadingReport, error } = useQuery({
    queryKey: ['aiqReport', 'individual', productId, classProductId],
    queryFn: async () => {
      if (!productId) return null
      const response = await aiqApi.getIndividualReport(productId, Number(classProductId))
      return response.data.data
    },
    enabled: !!productId,
  })

  if (isLoadingCourse || isLoadingReport) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-4" />
          <p className="text-gray-600">리포트를 불러오는 중...</p>
        </div>
      </div>
    )
  }

  if (error || !reportData) {
    const errorMessage = error instanceof Error ? error.message : '리포트를 불러오는데 실패했습니다'
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <p className="text-gray-800 font-medium mb-2">리포트 조회 실패</p>
          <p className="text-sm text-gray-500 mb-4">{errorMessage}</p>
          <button
            onClick={() => navigate('/my/aiq')}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            AIQ 페이지로 돌아가기
          </button>
        </div>
      </div>
    )
  }

  return (
    <AiqIndividualReportView
      data={reportData}
      productName={currentCourse?.productName ?? undefined}
      backButton={
        <button
          onClick={() => navigate('/my/aiq')}
          className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          AIQ 페이지로
        </button>
      }
    />
  )
}
